'use client';

/**
 * LeafRow — one placement-grain row under an expanded pivot group. Own
 * component (not inline in a `.map()`): needs `useAffordance` (rules of
 * hooks) and, for the unassigned row's room-assign picker, local state.
 *
 * A leaf row already resolves to exactly one (line, slot) placement, the
 * same identity whichever axis grouped the table — so its qty cell is
 * editable in every mode, unlike the group-header row (Line-mode only,
 * see `GroupHeaderRow`'s docstring). The Line-mode "unassigned remainder"
 * leaf (slot_sid null) has nothing to edit there — it gets the room-
 * assign picker instead (`isUnassignedLineRow`), the ONE place a line
 * gets its first room placement from canvas; every other leaf row
 * already IS a placement.
 *
 * @component_role: Card fragment
 * @component_type: Row renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import { RoomAssignPicker } from './room-assign-picker';
import { money, leafQuoteCell } from './costplan-pivot-shared';
import type { GroupByKey, PivotRow, VendorColumn, RoomSlotOption } from './costplan-pivot-shared';

const CELL = 'py-1.5 px-3';

function leafDetail(
  r: PivotRow, mode: GroupByKey, L: Record<string, string>, showProductIdentity: boolean,
): { title: string; subtitle: string } {
  if (mode === 'line') {
    // A multi-product group (see GroupHeaderRow's singleProduct) has no
    // single header that can speak for every row's product anymore —
    // each leaf needs its own identity, or "Co-Living/Studio" appearing
    // three times with three different values is indistinguishable.
    // Single-product groups already show this in the header subtitle,
    // so it's skipped here to avoid repeating it on every leaf.
    if (showProductIdentity) {
      const title = r.over_allocated
        ? (L.over_allocated ?? 'Over-allocated')
        : r.product_name ?? '—';
      const subtitle = r.over_allocated
        ? [r.product_sku, r.vendor_name].filter(Boolean).join(' · ')
        : [r.slot_name ?? (L.unassigned ?? ''), r.product_sku, r.vendor_name].filter(Boolean).join(' · ');
      return { title, subtitle };
    }
    const title = r.over_allocated
      ? (L.over_allocated ?? 'Over-allocated')
      : r.slot_name ?? (L.unassigned ?? '');
    return { title, subtitle: '' };
  }
  if (mode === 'slot') {
    return { title: r.product_name ?? '—', subtitle: [r.product_sku, r.vendor_name].filter(Boolean).join(' · ') };
  }
  return { title: r.product_name ?? '—', subtitle: [r.product_sku, r.slot_name].filter(Boolean).join(' · ') };
}

export function LeafRow({
  r, mode, L, vendorColumns, onEditPlacement, mutating, roomSlots, showProductIdentity,
}: {
  r: PivotRow; mode: GroupByKey; L: Record<string, string>; vendorColumns: VendorColumn[];
  onEditPlacement: (lineId: string, slotSid: string, qty: number) => void;
  mutating: boolean; roomSlots: RoomSlotOption[]; showProductIdentity: boolean;
}) {
  const { title, subtitle } = leafDetail(r, mode, L, showProductIdentity);
  const placementEditable = !!r.line_id && !!r.slot_sid;
  const cellId = placementEditable ? `${r.line_id}::${r.slot_sid}` : null;
  const placementQtyTestId = useAffordance('canvas.costplan_lines.edit_placement_qty', { cell_id: cellId ?? '' });
  const assignTestId = useAffordance('canvas.costplan_lines.assign_room', { line_id: r.line_id ?? '' });

  const isUnassignedLineRow = mode === 'line' && r.slot_sid == null && !!r.line_id;

  return (
    // Plain white row — the data a buyer is actually scanning (names,
    // prices) stays at full contrast; the group header carries the grey
    // tint instead (see GroupHeaderRow), so the HEADER is subordinate
    // chrome around the content, not the other way around. The left rule
    // is still the "thread" connecting every leaf back to the header it
    // expanded from — a structural indent, independent of which row gets
    // the tint.
    <tr className="border-b border-border/40">
      <td className={cn('py-1.5 pl-9 pr-3', 'border-l-2 border-border')}>
        <div className="text-[0.92em] text-foreground">{title}</div>
        {subtitle && (
          <div className={cn('font-mono text-[0.8em] truncate max-w-[280px]', text.muted)} title={subtitle}>{subtitle}</div>
        )}
        {isUnassignedLineRow && roomSlots.length > 0 && (
          <div className="mt-1">
            <RoomAssignPicker
              roomSlots={roomSlots}
              mutating={mutating}
              assignTestId={assignTestId}
              onAssign={(slotSid, qty) => r.line_id && onEditPlacement(r.line_id, slotSid, qty)}
              triggerLabel="+ Assign to a room"
              triggerClassName="text-[10.5px] text-primary hover:underline"
              wrapperClassName="flex items-center gap-1"
            />
          </div>
        )}
      </td>
      <td className={cn(CELL, 'text-right tabular-nums')} onClick={(e) => e.stopPropagation()}>
        {placementEditable ? (
          <input
            key={`${cellId}-${r.qty}`}
            data-testid={cellId ? placementQtyTestId : undefined}
            type="number"
            min="0"
            step="1"
            disabled={mutating}
            defaultValue={r.qty}
            onBlur={(e) => {
              const v = parseFloat(e.target.value);
              if (r.line_id && r.slot_sid && !Number.isNaN(v) && v >= 0 && v !== r.qty) {
                onEditPlacement(r.line_id, r.slot_sid, v);
              }
            }}
            className="w-14 text-right tabular-nums bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1 py-0.5 focus:outline-none"
          />
        ) : (
          <span className={r.over_allocated ? 'text-warning font-medium' : undefined}>
            {r.over_allocated ? `+${Math.abs(r.qty).toLocaleString()}` : r.qty.toLocaleString()}
          </span>
        )}
      </td>
      <td className={cn(CELL, 'text-right tabular-nums')}>{money(r.value)}</td>
      {vendorColumns.length > 0 ? (
        // Leaf rows are room-placement breakdowns of the SAME product a
        // Line-mode group already carries one quote set for — repeating
        // it per placement would just be noise; the comparison lives on
        // the group header.
        vendorColumns.map((vc) => <td key={vc.vendor_sid} className={CELL} />)
      ) : (
        <td className={cn(CELL, 'text-right tabular-nums', r.reaches_quote && 'text-primary')}>
          {leafQuoteCell(r)}
        </td>
      )}
      <td className={CELL} />
    </tr>
  );
}
